import { useFieldArray, useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import api from "../../../../services/api";
import { useNavigate } from "react-router-dom";

const paymentInstallmentSchema = z.object({
  name: z.string().min(1, "O nome é obrigatório"),
  paymentInstallments: z.array(
    z.object({
      percentage: z.coerce
        .number()
        .positive("A porcentagem deve ser um valor positivo"),
      matchLeadTime: z.boolean(),
      paymentIn: z.coerce
        .number()
        .nonnegative("O prazo de pagamento deve ser um número não negativo"),
    })
  ),
});

function CreatePaymentMethodPage() {
  const [errorMessage, setErrorMessage] = useState(null);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(paymentInstallmentSchema),
    defaultValues: {
      paymentInstallments: [
        { percentage: "", matchLeadTime: false, paymentIn: 0 },
      ],
    },
  });

  const { fields, replace } = useFieldArray({
    name: "paymentInstallments",
    control,
  });

  const handleSelectChange = (e) => {
    const count = parseInt(e.target.value, 10);
    const newFields = Array.from({ length: count }, () => ({
      percentage: "",
      matchLeadTime: false,
      paymentIn: 0,
    }));
    replace(newFields);
  };

  const onFormSubmit = async (values) => {
    try {
      setErrorMessage(null);
      const paymentInstallmentsPercentages = values.paymentInstallments.map(
        (item) => item.percentage
      );

      const percentagesSum = paymentInstallmentsPercentages.reduce(
        (prev, next) => prev + next
      );

      if (percentagesSum !== 100)
        throw new Error("O somatório das porcentagens deve ser 100%");

      await api.post("/payment-methods", values);
      navigate("/configuracoes");
    } catch (error) {
      console.log("Erro ao criar método de pagamento: ", error);
      setErrorMessage(
        error.message || "Não foi possível criar forma de pagamento"
      );
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center">
      <div className="bg-gray-200 p-4 rounded-md">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className=" overflow-y-auto max-h-[450px] custom-scrollbar pr-4 w-[600px]">
            <div className="flex justify-between mx-2 pb-4 items-center">
              <div className="flex gap-2 items-center">
                <label>Nome: </label>
                <input {...register("name")}></input>
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name.message}</p>
                )}
              </div>
              <select
                onChange={handleSelectChange}
                className=" p-2 border rounded-md"
              >
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <option key={num} value={num}>
                    {num} parcela{num > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="mb-4 border border-gray-400 rounded-md p-6"
              >
                <div>
                  <label className="block mb-1">
                    Porcentagem (Parcela {index + 1}):
                  </label>
                  <input
                    {...register(`paymentInstallments.${index}.percentage`)}
                    type="number"
                    step="0.01"
                    className="p-2 border rounded-md w-full"
                  />
                  {errors.paymentInstallments?.[index]?.percentage && (
                    <p className="text-red-500 text-sm">
                      {errors.paymentInstallments[index].percentage.message}
                    </p>
                  )}
                </div>
                <div className="flex gap-4">
                  <div className="mt-2">
                    <label className="block mb-1">
                      Prazo de Pagamento (dias):
                    </label>
                    <input
                      {...register(`paymentInstallments.${index}.paymentIn`)}
                      type="number"
                      className="p-2 border rounded-md w-full"
                    />
                    {errors.paymentInstallments?.[index]?.paymentIn && (
                      <p className="text-red-500 text-sm">
                        {errors.paymentInstallments[index].paymentIn.message}
                      </p>
                    )}
                  </div>
                  <div className="mt-2 flex gap-2 items-center">
                    <input
                      {...register(
                        `paymentInstallments.${index}.matchLeadTime`
                      )}
                      type="checkbox"
                      className="p-2"
                    />
                    <label className="block mb-1">Igualar ao Lead Time</label>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center">
            <button
              type="submit"
              className="bg-blue-500 text-white px-4 py-2 rounded-md"
            >
              Salvar
            </button>
            {errorMessage && <p className="text-red-500">{errorMessage}</p>}
          </div>
        </form>
      </div>
    </section>
  );
}

export default CreatePaymentMethodPage;
